import {SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyInputPurpose, SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {SurveyAnswerDraft} from "../runner/questions/runnerQuestion.types";
import {surveyFormatIsoDate} from "./surveyQuestions.util";
import {getSurveyQuestionTypeFacts} from "./surveyQuestionTypeFacts";

export type SurveyFieldProblem = {
    key: string
    values?: Record<string, string | number>
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const isSet = <T>(value: T | undefined | null): value is T => value !== undefined && value !== null;

const hasText = (value?: string) => !!value?.trim();

const outsideScale = (type: SurveyQuestionType, value: number | undefined) => {
    const scale = getSurveyQuestionTypeFacts(type)?.scale;
    return !!scale && isSet(value) && (value < scale.min || value > scale.max);
};

export const isAnswered = (draft?: SurveyAnswerDraft): boolean => !!draft && (
    hasText(draft.textValue)
    || isSet(draft.scaleValue)
    || isSet(draft.rateValue)
    || isSet(draft.numberValue)
    || hasText(draft.dateValue)
    || isSet(draft.booleanValue)
    || !!draft.optionIds?.length
    || hasText(draft.customText)
    || !!draft.mediaValues?.length
    || !!draft.localFiles?.length
    || !!draft.signatureValue
);

export const surveyAnswerProblem = (
    question: SurveyQuestion,
    draft: SurveyAnswerDraft | undefined
): SurveyFieldProblem | undefined => {
    const answered = isAnswered(draft);
    const legal = question.type === SurveyQuestionType.LEGAL;

    if (question.isRequired && !answered) {
        return legal ? {key: 'Please accept to continue'} : {key: 'Please answer this question'};
    }
    if (!draft) return undefined;

    switch (question.type) {
        case SurveyQuestionType.CHECKLIST: {
            if (!question.isRequired) break;

            const ticked = draft.optionIds ?? [];
            const missing = (question.options ?? [])
                .map((option, index) => option.id ?? `option-${index}`)
                .some(optionId => !ticked.includes(optionId));

            if (missing) return {key: 'Please confirm every item'};
            break;
        }

        case SurveyQuestionType.LEGAL: {
            if (question.isRequired && answered && draft.booleanValue !== true) {
                return {key: 'Please accept to continue'};
            }
            if (question.requireSignature && draft.booleanValue === true
                && !draft.signatureValue?.mediaURI && !draft.signatureDrawn) {
                return {key: 'Please sign to continue'};
            }
            break;
        }

        case SurveyQuestionType.NUMBER: {
            const value = draft.numberValue;
            if (!isSet(value)) break;

            const min = question.minNumber;
            const max = question.maxNumber;
            const belowMin = isSet(min) && value < min;
            const aboveMax = isSet(max) && value > max;
            if (!belowMin && !aboveMax) break;

            if (isSet(min) && isSet(max)) {
                return {key: 'Please enter a number between {{from}} and {{to}}', values: {from: min, to: max}};
            }
            return isSet(min)
                ? {key: 'Please enter {{min}} or more', values: {min}}
                : {key: 'Please enter {{max}} or less', values: {max: max as number}};
        }

        case SurveyQuestionType.DATE: {
            const value = draft.dateValue;
            if (!hasText(value)) break;

            const min = question.minDate;
            const max = question.maxDate;
            const beforeMin = hasText(min) && value! < min!;
            const afterMax = hasText(max) && value! > max!;
            if (!beforeMin && !afterMax) break;

            const from = surveyFormatIsoDate(min);
            const to = surveyFormatIsoDate(max);

            if (from && to) return {key: 'Please pick a date between {{from}} and {{to}}', values: {from, to}};
            return from
                ? {key: 'Please pick a date from {{from}}', values: {from}}
                : {key: 'Please pick a date up to {{to}}', values: {to}};
        }

        case SurveyQuestionType.GENERIC_SCORE:
            if (outsideScale(question.type, draft.scaleValue)) return {key: 'Please check this answer'};
            break;

        case SurveyQuestionType.GENERIC_STARS:
            if (outsideScale(question.type, draft.rateValue)) return {key: 'Please check this answer'};
            break;

        // No rule of their own: the required check above and the email check below are all they get.
        case SurveyQuestionType.WELCOME:
        case SurveyQuestionType.MULTIPLE_CHOICE:
        case SurveyQuestionType.PICTURE_CHOICE:
        case SurveyQuestionType.DROPDOWN:
        case SurveyQuestionType.YES_NO:
        case SurveyQuestionType.SHORT_TEXT:
        case SurveyQuestionType.LONG_TEXT:
        case SurveyQuestionType.UPLOAD_MEDIA:
        case SurveyQuestionType.END_SCREEN:
        case undefined:
            break;

        default: {
            // A new enum member fails the typecheck here; a type from a newer API still gets the email check.
            const unhandled: never = question.type;
            break;
        }
    }

    if (question.inputPurpose === SurveyInputPurpose.EMAIL && hasText(draft.textValue)
        && !EMAIL_PATTERN.test(draft.textValue!.trim())) {
        return {key: 'Please enter a valid email address'};
    }

    return undefined;
};

export const surveyAnswerProblems = (
    questions: SurveyQuestion[],
    answers: Record<string, SurveyAnswerDraft | undefined>
): Record<string, SurveyFieldProblem> => {
    const problems: Record<string, SurveyFieldProblem> = {};

    questions.forEach(question => {
        if (!question.id) return;

        const problem = surveyAnswerProblem(question, answers[question.id]);
        if (problem) problems[question.id] = problem;
    });

    return problems;
};

